// src/components/DashboardCanvas.jsx
import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { Target, Zap, MessageCircle, Crown } from 'lucide-react';

// Importar componentes existentes que se mantendrán
import WelcomeHero from './WelcomeHero';
import MessageBubble from './MessageBubble';
import InputWrapper from './InputWrapper';
import BannerAsistente from './BannerAsistente';
import EmptyStateSuggestions from './EmptyStateSuggestions';
import RolPlayLobby from './RolPlayLobby';
import RolPlayCard from './RolPlayCard';
import DashboardLanding from './DashboardLanding';

// Importar configuraciones
import asistentesConfig from '../constants/asistentesConfig';

const DashboardCanvas = ({
  // Props que vienen del DashboardLayout
  selectedAssistant,
  selectedChatId,
  loading,
  isCreatingNewChat,
  onCreateRolPlayChat,
  onBackToDashboard
}) => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const messagesEndRef = useRef(null);
  
  // Estados locales del canvas
  const [messages, setMessages] = useState([]);
  const [inputMessage, setInputMessage] = useState("");
  const [isTyping, setIsTyping] = useState(false);
  const [isChatInputEnabled, setIsChatInputEnabled] = useState(false);
  const [rolPlayView, setRolPlayView] = useState('lobby');
  
  // Configuraciones de RolPlay
  const rolPlayCategories = [
    {
      id: 'reuniones',
      title: 'Reuniones Difíciles',
      icon: <Target size={20} />,
      description: 'Maneja reuniones complejas con stakeholders conflictivos',
      difficulty: 'Intermedio',
      duration: '15-20 min',
      bgClass: 'bg-gradient-to-br from-blue-500 to-purple-600'
    },
    {
      id: 'conflictos',
      title: 'Resolución de Conflictos',
      icon: <Zap size={20} />,
      description: 'Negocia y resuelve disputas entre equipos',
      difficulty: 'Avanzado',
      duration: '20-25 min',
      bgClass: 'bg-gradient-to-br from-red-500 to-orange-600'
    },
    {
      id: 'feedback',
      title: 'Conversaciones Difíciles',
      icon: <MessageCircle size={20} />,
      description: 'Da feedback constructivo en situaciones complejas',
      difficulty: 'Intermedio',
      duration: '10-15 min',
      bgClass: 'bg-gradient-to-br from-green-500 to-teal-600'
    },
    {
      id: 'liderazgo',
      title: 'Liderazgo bajo Presión',
      icon: <Crown size={20} />,
      description: 'Toma decisiones críticas con información limitada',
      difficulty: 'Avanzado',
      duration: '25-30 min',
      bgClass: 'bg-gradient-to-br from-purple-500 to-pink-600'
    }
  ];
  
  // Usuario actual
  const currentUserName = user?.nombre || user?.name || localStorage.getItem('userName') || "Usuario";
  
  // Auto-scroll para mensajes
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, isTyping]);
  
  // Funciones de manejo
  const handleOptionClick = (opcion) => {
    const cleanedOption = opcion.replace(/^\d+\.\s*/, '').trim();
    setInputMessage(cleanedOption);
  };
  
  const handleSelectRolPlay = (categoria) => {
    console.log('Categoría seleccionada:', categoria);
    // Implementar lógica específica para cada categoría
  };
  
  const handleLobbyOptionSelect = (option) => {
    if (option === 'simulaciones') {
      setRolPlayView('tarjetas');
    } else if (option === 'historial') {
      console.log('Ver historial de simulaciones');
    }
  };
  
  const handleSendMessage = () => {
    // Esta función se implementará según la lógica necesaria
    console.log('Enviar mensaje:', inputMessage);
  };

  return (
    <div className="flex flex-1 flex-col overflow-y-auto bg-gray-50">
      {/* Área principal de contenido */}
      <div className={`flex-1 ${!selectedAssistant ? '' : 'px-4'}`}>
        
        {/* Vista Dashboard Landing con Cards EPL */}
        {!selectedAssistant ? (
          // Dashboard Landing - Experiencia guiada con laboratorios EPL
          <DashboardLanding userName={currentUserName} />
        ) : selectedAssistant === "rolplay" ? (
          // Vista RolPlay
          !selectedChatId ? (
            rolPlayView === 'lobby' ? (
              <RolPlayLobby 
                onSelectOption={handleLobbyOptionSelect}
              />
            ) : (
              <>
                <h2 className="text-2xl font-bold mb-4">Selecciona una simulación</h2>
                <p className="mb-6 text-gray-700">
                  Aquí no vienes a improvisar.<br />
                  Entrenarás como los líderes que toman decisiones bajo presión real.<br />
                  Tu coach de IA está calibrado para exigirte como en la vida real.
                </p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {rolPlayCategories.map((cat, idx) => (
                    <RolPlayCard
                      key={idx}
                      category={cat}
                      onClick={() => handleSelectRolPlay(cat)}
                    />
                  ))}
                </div>
              </>
            )
          ) : (
            // Vista de chat activo en RolPlay
            <>
              {messages.map((msg, index) => (
                <MessageBubble
                  key={index}
                  message={msg}
                  userName={currentUserName}
                />
              ))}
              <div ref={messagesEndRef} />
            </>
          )
        ) : (
          // Vista para otros asistentes (PMFAST, CRISIS, COMUNICA, etc.)
          <>
            {messages.map((msg, index) => (
              <MessageBubble
                key={index}
                message={msg}
                userName={currentUserName}
              />
            ))}
            <div ref={messagesEndRef} />
            
            {/* Estado con chat seleccionado pero sin mensajes */}
            {selectedChatId && messages.length === 0 && !isTyping && !isCreatingNewChat && (
              <div className="flex flex-1 flex-col items-center justify-center">
                {asistentesConfig[selectedAssistant] && (
                  <BannerAsistente
                    title={asistentesConfig[selectedAssistant]?.banner?.title}
                    subtitle={asistentesConfig[selectedAssistant]?.banner?.subtitle}
                    bgColor={asistentesConfig[selectedAssistant]?.banner?.bg}
                  />
                )}
                <EmptyStateSuggestions 
                  assistant={selectedAssistant} 
                  onSelect={handleOptionClick} 
                />
              </div>
            )}
            
            {/* Indicador de escritura */}
            {isTyping && (
              <MessageBubble
                message={{ isTyping: true }}
              />
            )}
          </>
        )}
      </div>

      {/* Área de input - Solo mostrar si hay asistente seleccionado */}
      {selectedAssistant && (isChatInputEnabled || (selectedAssistant === "rolplay" && selectedChatId)) && (
        <InputWrapper
          inputMessage={inputMessage || ""}
          setInputMessage={setInputMessage}
          handleSendMessage={handleSendMessage}
          selectedAssistant={selectedAssistant}
          selectedChatId={selectedChatId || ""}
          showInputArea={true}
          isProcessing={isTyping}
        />
      )}
    </div>
  );
};

export default DashboardCanvas;